export type Value = string | number | bigint | boolean | null | undefined; // non-Collection types
export type Collection<T> = Exclude<Iterable<T>, string>;

export interface SingleValueParam {
  field: string;
  op: '=' | '!=' | 'ilike' | 'like';
  value: Value;
}

export interface MultiValueParam {
  field: string;
  op: 'in' | '!in';
  value: Collection<Value>;
}

export type Param = SingleValueParam | MultiValueParam;
// TODO check there's at least 2 elements in the array
export type Condition = ['and' | 'or', ...(Param | Condition)[]];

export function filter (...params: (Condition | Param)[]): string | undefined {
  if (params.length === 0) {
    return;
  }
  if (params.length === 1) {
    return formatParam(params[0]);
  }
  // Multiple parameters are joined with AND by default
  return formatParam(['and', ...params]);
}

function isCollection (value: any): value is Iterable<any> {
  return value != null && typeof value != 'string' && typeof value[Symbol.iterator] === 'function';
}

function formatParam (param: Condition | Param): string {
  // Check if it's a condition (array)
  if (Array.isArray(param)) {
    const [operator, ...conditions] = param;
    const formattedConditions = conditions.map(formatParam).join(` ${operator.toUpperCase()} `);
    return conditions.length > 1 ? `(${formattedConditions})` : formattedConditions;
  }
  // It's a parameter object
  const { field, op, value } = param;
  // Handle array values for MultiValueParam
  if (!isCollection(value)) {
    return `${field} ${op} ${formatValue(value)}`;
  }
  // TODO use Iterator once Iterator.prototype.map is available https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Iterator/map
  const arr = Array.from(value);
  if (op === 'in') {
    return formatParam(['or', ...arr.map((value): SingleValueParam => ({ field, op: '=', value }))]);
  } else if (op === '!in') {
    return formatParam(['and', ...arr.map((value): SingleValueParam => ({ field, op: '!=', value }))]);
  }
  throw `Unsupported operator for array values. op: ${op} value: ${value}`;
}

function formatValue (value: Value): string {
  if (value === null) {
    return 'null';
  }
  if (value === undefined) {
    return 'undefined';
  }
  return String(value);
}

export function simpleFilter (params: Record<string, any>): string | undefined {
  return filter(
    ...Object.entries(params).map(
      ([field, value]): Param =>
        ({
          field,
          op: isCollection(value) ? 'in' : '=',
          value
        })));
}
